import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";

export interface LoaderProps {
  /** `centered` fills its row and pads it (page/section loading); `inline` sits in text. */
  variant?: "centered" | "inline";
  /** Spinner diameter in px; 40 centered, 16 inline. */
  size?: number;
  /** Announced text; defaults to "Loading…". */
  label?: string;
}

/** A polite live-region spinner whose name is announced to assistive tech. */
const Loader = ({ variant = "centered", size, label }: LoaderProps) => {
  const { t } = useTranslation();
  const inline = variant === "inline";
  return (
    <Box
      role="status"
      aria-live="polite"
      data-variant={variant}
      sx={
        inline
          ? {
              display: "inline-flex",
              alignItems: "center",
              verticalAlign: "middle",
            }
          : {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              p: 3,
            }
      }
    >
      <CircularProgress
        aria-hidden
        size={size ?? (inline ? 16 : 40)}
        thickness={inline ? 5 : 4}
        sx={{ color: inline ? "currentColor" : "primary.main" }}
      />
      <Box component="span" sx={visuallyHidden}>
        {label ?? t("common.loading")}
      </Box>
    </Box>
  );
};

export default Loader;
